/** @forge-header
 * @id          moneylineGameRow
 * @role        Single game row + ladder expansion | trade click construction for both venues
 * @pattern     component
 * @tier        T2
 * @purity      none
 * @units       cents
 * @money-path  yes
 * @fan-out     ~1
 * @upstream    nbaConsolidated/types:ConsolidatedGameBooks,TeamSide
 *              | nbaConsolidated/pricing:applyTakerFee,computeOrderPriceFromRawBid,getTeamWinData,next3NoLadders,next3YesLadders,takerFeeForVenue,liquidityDollars
 *              | nbaConsolidated/format:formatDualPriceCents
 *              | formatters:formatStartTimePt | ./tradeTypes:TradeClickRequest
 * @downstream  ConsolidatedMoneylines
 * @exports     MoneylineGameRow:component
 * @test        untested
 * @invariant   Kalshi pricing parity | team wins = opponent NO book | same as TwoRowMoneylines
 */

import { cn } from '@/lib/utils';
import { LiquidityBar } from '@/components/atoms/LiquidityBar';
import { Td } from '@/components/atoms/TableCells';
import type { ConsolidatedGameBooks, TeamSide } from '@/lib/nbaConsolidated/types';
import { formatDualPriceCents } from '@/lib/nbaConsolidated/format';
import { formatStartTimePt } from '@/lib/formatters';
import {
  applyTakerFee,
  computeOrderPriceFromRawBid,
  getTeamWinData,
  next3NoLadders,
  next3YesLadders,
  takerFeeForVenue,
  liquidityDollars,
} from '@/lib/nbaConsolidated/pricing';
import type { OddsMode } from './DashboardHeader';
import type { TradeClickRequest } from './tradeTypes';

interface MoneylineGameRowProps {
  game: ConsolidatedGameBooks;
  oddsMode: OddsMode;
  maxLiquidity: number;
  expanded: boolean;
  onToggleExpand: (gameKey: string) => void;
  onTradeClick?: (req: TradeClickRequest) => void;
  onNavigateToMarket?: (marketTicker: string) => void;
}

export function MoneylineGameRow({
  game,
  oddsMode,
  maxLiquidity,
  expanded,
  onToggleExpand,
  onTradeClick,
  onNavigateToMarket,
}: MoneylineGameRowProps) {
  const isTakerMode = oddsMode === 'taker';
  const awayName = game.awayName ?? game.awayCode;
  const homeName = game.homeName ?? game.homeCode;

  const awayK = getTeamWinData({
    game: game.kalshi ?? null,
    venue: 'kalshi',
    teamSide: 'away',
    isTakerMode,
  });
  const homeK = getTeamWinData({
    game: game.kalshi ?? null,
    venue: 'kalshi',
    teamSide: 'home',
    isTakerMode,
  });
  const awayP = getTeamWinData({
    game: game.polymarket ?? null,
    venue: 'polymarket',
    teamSide: 'away',
    isTakerMode,
  });
  const homeP = getTeamWinData({
    game: game.polymarket ?? null,
    venue: 'polymarket',
    teamSide: 'home',
    isTakerMode,
  });

  const awayKDollars = liquidityDollars(awayK.liq, awayK.priceCents);
  const homeKDollars = liquidityDollars(homeK.liq, homeK.priceCents);
  const awayPDollars = liquidityDollars(awayP.liq, awayP.priceCents);
  const homePDollars = liquidityDollars(homeP.liq, homeP.priceCents);

  const awayKDisplay = formatDualPriceCents(awayK.priceCents);
  const homeKDisplay = formatDualPriceCents(homeK.priceCents);
  const awayPDisplay = formatDualPriceCents(awayP.priceCents);
  const homePDisplay = formatDualPriceCents(homeP.priceCents);

  const handleTrade = (teamSide: TeamSide, _e: React.MouseEvent) => {
    const top = teamSide === 'away' ? awayK : homeK;
    const marketTicker = top.marketTicker;
    if (!marketTicker || !onTradeClick) return;

    const teamLabel = teamSide === 'away' ? awayName : homeName;
    const orderPrice = computeOrderPriceFromRawBid(top.rawBidPriceCents, isTakerMode);
    if (orderPrice === null) return;
    onTradeClick({
      venue: 'kalshi',
      marketTicker,
      side: 'no',
      priceCents: orderPrice,
      contracts: 100,
      isTakerMode,
      teamLabel,
      gameKey: game.key,
      gameDate: game.date,
      startTimePt: game.startTimePt,
    });
  };

  const handlePolyTrade = (teamSide: TeamSide, _e: React.MouseEvent) => {
    const top = teamSide === 'away' ? awayP : homeP;
    const marketTicker = top.marketTicker;
    if (!marketTicker || !onTradeClick) return;

    const teamLabel = teamSide === 'away' ? awayName : homeName;
    const orderPrice = computeOrderPriceFromRawBid(top.rawBidPriceCents, isTakerMode);
    if (orderPrice === null) return;

    // Team win is expressed as NO on opponent.
    const opponentMarket =
      teamSide === 'away' ? game.polymarket?.markets.home : game.polymarket?.markets.away;

    onTradeClick({
      venue: 'polymarket',
      marketTicker,
      tokenId: opponentMarket?.tokenId,
      conditionId: opponentMarket?.conditionId,
      tickSize: opponentMarket?.tickSize,
      negRisk: opponentMarket?.negRisk,
      side: 'no',
      priceCents: orderPrice,
      contracts: 100,
      isTakerMode,
      teamLabel,
      gameKey: game.key,
      gameDate: game.date,
      startTimePt: game.startTimePt,
    });
  };

  const handleLadderTrade = (
    teamSide: TeamSide,
    ladderPrice: number | null,
    marketTicker: string | null,
    e: React.MouseEvent
  ) => {
    if (!marketTicker) return;
    e.stopPropagation();

    // Primary click: open trade modal with ladder price
    if (onTradeClick && ladderPrice !== null) {
      const teamLabel = teamSide === 'away' ? awayName : homeName;
      onTradeClick({
        venue: 'kalshi',
        marketTicker,
        side: 'no',
        priceCents: ladderPrice,
        contracts: 100,
        isTakerMode,
        teamLabel,
        gameKey: game.key,
        gameDate: game.date,
        startTimePt: game.startTimePt,
      });
      return;
    }

    // Fallback: navigate to market detail
    if (onNavigateToMarket) {
      onNavigateToMarket(marketTicker);
    }
  };

  const handleLadderPolyTrade = (
    teamSide: TeamSide,
    ladderPrice: number | null,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    const opponentMarket =
      teamSide === 'away' ? game.polymarket?.markets.home : game.polymarket?.markets.away;

    // Primary click: open trade modal with ladder price
    if (onTradeClick && ladderPrice !== null) {
      const teamLabel = teamSide === 'away' ? awayName : homeName;
      if (!opponentMarket?.tokenId) return;

      onTradeClick({
        venue: 'polymarket',
        marketTicker: `asset:${opponentMarket.tokenId}`,
        tokenId: opponentMarket.tokenId,
        conditionId: opponentMarket.conditionId,
        tickSize: opponentMarket.tickSize,
        negRisk: opponentMarket.negRisk,
        side: 'no',
        priceCents: ladderPrice,
        contracts: 100,
        isTakerMode,
        teamLabel,
        gameKey: game.key,
        gameDate: game.date,
        startTimePt: game.startTimePt,
      });
      return;
    }

    // Fallback: navigate to market detail
    const marketTicker = opponentMarket?.tokenId ? `asset:${opponentMarket.tokenId}` : null;
    if (marketTicker && onNavigateToMarket) {
      onNavigateToMarket(marketTicker);
    }
  };

  const renderLadderRow = (idx: number) => {
    // In maker mode, ladder comes from NO bids; in taker mode, from YES bids (for inversion).
    const awayLvls = isTakerMode
      ? next3YesLadders(awayK.yesLevels, awayK.rawBidPriceCents)
      : next3NoLadders(awayK.noLevels, awayK.rawBidPriceCents);
    const homeLvls = isTakerMode
      ? next3YesLadders(homeK.yesLevels, homeK.rawBidPriceCents)
      : next3NoLadders(homeK.noLevels, homeK.rawBidPriceCents);
    const awayPLvls = isTakerMode
      ? next3YesLadders(awayP.yesLevels, awayP.rawBidPriceCents)
      : next3NoLadders(awayP.noLevels, awayP.rawBidPriceCents);
    const homePLvls = isTakerMode
      ? next3YesLadders(homeP.yesLevels, homeP.rawBidPriceCents)
      : next3NoLadders(homeP.noLevels, homeP.rawBidPriceCents);

    const awayLvl = awayLvls[idx] ?? null;
    const homeLvl = homeLvls[idx] ?? null;
    const awayPLvl = awayPLvls[idx] ?? null;
    const homePLvl = homePLvls[idx] ?? null;

    const feeRate = takerFeeForVenue('kalshi');
    const awayPrice = awayLvl
      ? isTakerMode
        ? applyTakerFee(100 - awayLvl.priceCents, feeRate)
        : awayLvl.priceCents
      : null;
    const homePrice = homeLvl
      ? isTakerMode
        ? applyTakerFee(100 - homeLvl.priceCents, feeRate)
        : homeLvl.priceCents
      : null;
    const polyFee = takerFeeForVenue('polymarket');
    const awayPPrice = awayPLvl
      ? isTakerMode
        ? applyTakerFee(100 - awayPLvl.priceCents, polyFee)
        : awayPLvl.priceCents
      : null;
    const homePPrice = homePLvl
      ? isTakerMode
        ? applyTakerFee(100 - homePLvl.priceCents, polyFee)
        : homePLvl.priceCents
      : null;

    const awayDollars = awayLvl ? liquidityDollars(awayLvl.size, awayPrice) : null;
    const homeDollars = homeLvl ? liquidityDollars(homeLvl.size, homePrice) : null;
    const awayPD = awayPLvl ? liquidityDollars(awayPLvl.size, awayPPrice) : null;
    const homePD = homePLvl ? liquidityDollars(homePLvl.size, homePPrice) : null;

    return (
      <tr
        key={`${game.key}-ladder-${idx}`}
        className=""
      >
        <Td> </Td>
        <Td className="font-mono text-[10px]"> </Td>
        <Td className="font-mono text-[10px]"> </Td>
        <Td className="text-xs"> </Td>
        <Td className="text-xs"> </Td>
        {/* Kalshi ladder prices */}
        <Td
          className={cn('text-right font-mono', awayPrice !== null && 'hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            handleLadderTrade('away', awayPrice, awayK.marketTicker, e);
          }}
          title={awayK.marketTicker && awayPrice !== null ? 'Click to trade' : undefined}
        >
          {formatDualPriceCents(awayPrice)}
        </Td>
        <Td
          className={cn('text-right font-mono', homePrice !== null && 'hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            handleLadderTrade('home', homePrice, homeK.marketTicker, e);
          }}
          title={homeK.marketTicker && homePrice !== null ? 'Click to trade' : undefined}
        >
          {formatDualPriceCents(homePrice)}
        </Td>
        {/* Kalshi ladder volume */}
        <Td className="text-right font-mono text-xs">
          {awayLvl ? awayLvl.size.toLocaleString() : ''}
        </Td>
        <Td className="text-right font-mono text-xs">
          {homeLvl ? homeLvl.size.toLocaleString() : ''}
        </Td>
        {/* Kalshi ladder liquidity */}
        <Td>
          <LiquidityBar
            value={awayDollars ?? 0}
            maxValue={maxLiquidity}
          />
        </Td>
        <Td>
          <LiquidityBar
            value={homeDollars ?? 0}
            maxValue={maxLiquidity}
          />
        </Td>
        {/* Poly ladder prices */}
        <Td
          className={cn('text-right font-mono', awayPPrice !== null && 'hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            handleLadderPolyTrade('away', awayPPrice, e);
          }}
          title={awayPPrice !== null ? 'Click to trade' : undefined}
        >
          {formatDualPriceCents(awayPPrice)}
        </Td>
        <Td
          className={cn('text-right font-mono', homePPrice !== null && 'hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            handleLadderPolyTrade('home', homePPrice, e);
          }}
          title={homePPrice !== null ? 'Click to trade' : undefined}
        >
          {formatDualPriceCents(homePPrice)}
        </Td>
        {/* Poly ladder volume */}
        <Td className="text-right font-mono text-xs">
          {awayPLvl ? awayPLvl.size.toLocaleString() : ''}
        </Td>
        <Td className="text-right font-mono text-xs">
          {homePLvl ? homePLvl.size.toLocaleString() : ''}
        </Td>
        {/* Poly ladder liquidity */}
        <Td>
          <LiquidityBar
            value={awayPD ?? 0}
            maxValue={maxLiquidity}
          />
        </Td>
        <Td>
          <LiquidityBar
            value={homePD ?? 0}
            maxValue={maxLiquidity}
          />
        </Td>
      </tr>
    );
  };

  return (
    <>
      <tr
        className="hover:bg-accent/50 cursor-pointer transition-colors"
        onClick={() => onToggleExpand(game.key)}
      >
        <Td className="text-muted-foreground font-mono text-xs">
          {game.awayRotation != null && game.homeRotation != null
            ? `${game.awayRotation}/${game.homeRotation}`
            : '—'}
        </Td>
        <Td className="text-muted-foreground font-mono text-xs">{game.date}</Td>
        <Td className="text-muted-foreground font-mono text-xs">
          {formatStartTimePt(game.startTimePt)}
        </Td>
        <Td className="font-medium">{awayName}</Td>
        <Td className="font-medium">{homeName}</Td>
        {/* Kalshi prices */}
        <Td
          className={cn('text-right font-mono', awayK.priceCents !== null && 'hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            handleTrade('away', e);
          }}
          title={awayK.marketTicker && awayK.priceCents !== null ? 'Click to trade' : undefined}
        >
          {awayKDisplay}
        </Td>
        <Td
          className={cn('text-right font-mono', homeK.priceCents !== null && 'hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            handleTrade('home', e);
          }}
          title={homeK.marketTicker && homeK.priceCents !== null ? 'Click to trade' : undefined}
        >
          {homeKDisplay}
        </Td>
        {/* Kalshi volume (contracts) */}
        <Td className="text-right font-mono text-xs">
          {awayK.liq != null ? awayK.liq.toLocaleString() : '—'}
        </Td>
        <Td className="text-right font-mono text-xs">
          {homeK.liq != null ? homeK.liq.toLocaleString() : '—'}
        </Td>
        {/* Kalshi liquidity ($) */}
        <Td>
          <LiquidityBar
            value={awayKDollars ?? 0}
            maxValue={maxLiquidity}
          />
        </Td>
        <Td>
          <LiquidityBar
            value={homeKDollars ?? 0}
            maxValue={maxLiquidity}
          />
        </Td>
        {/* Poly prices */}
        <Td
          className={cn('text-right font-mono', awayP.priceCents !== null && 'hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            handlePolyTrade('away', e);
          }}
          title={awayP.marketTicker && awayP.priceCents !== null ? 'Click to trade' : undefined}
        >
          {awayPDisplay}
        </Td>
        <Td
          className={cn('text-right font-mono', homeP.priceCents !== null && 'hover:underline')}
          onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
            handlePolyTrade('home', e);
          }}
          title={homeP.marketTicker && homeP.priceCents !== null ? 'Click to trade' : undefined}
        >
          {homePDisplay}
        </Td>
        {/* Poly volume (contracts) */}
        <Td className="text-right font-mono text-xs">
          {awayP.liq != null ? awayP.liq.toLocaleString() : '—'}
        </Td>
        <Td className="text-right font-mono text-xs">
          {homeP.liq != null ? homeP.liq.toLocaleString() : '—'}
        </Td>
        {/* Poly liquidity ($) */}
        <Td>
          <LiquidityBar
            value={awayPDollars ?? 0}
            maxValue={maxLiquidity}
          />
        </Td>
        <Td>
          <LiquidityBar
            value={homePDollars ?? 0}
            maxValue={maxLiquidity}
          />
        </Td>
      </tr>

      {expanded && (
        <>
          {renderLadderRow(0)}
          {renderLadderRow(1)}
          {renderLadderRow(2)}
        </>
      )}
    </>
  );
}
